import { useT } from '@/lib/i18n';
import { type OpenedPdf, openPdf } from '@/lib/pdf/extract';
import { useSourceStore } from '@/lib/state/sourceStore';
import { useCallback, useEffect, useState } from 'react';

interface PdfUploaderProps {
  onDone?: () => void;
}

interface StagedPdf {
  file: File;
  doc: OpenedPdf;
  thumb: string | null;
  pageCount: number;
}

export function PdfUploader({ onDone }: PdfUploaderProps) {
  const { t } = useT();
  const addSource = useSourceStore((s) => s.addSource);
  const [staged, setStaged] = useState<StagedPdf | null>(null);
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cleanup any opened PDF when unmounting
  useEffect(() => {
    return () => {
      staged?.doc.destroy().catch(() => undefined);
    };
  }, [staged]);

  const onFile = useCallback(
    async (file: File) => {
      setError(null);
      setLoading(true);
      try {
        // Tear down any prior staged doc
        setStaged((prev) => {
          prev?.doc.destroy().catch(() => undefined);
          return null;
        });
        const doc = await openPdf(file);
        let thumb: string | null = null;
        try {
          thumb = await doc.renderThumbnail(1, 200);
        } catch {
          thumb = null;
        }
        setStaged({ file, doc, thumb, pageCount: doc.pageCount });
        setStartPage(1);
        setEndPage(Math.min(doc.pageCount, 10));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('input.pdf.err_load'));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  const extract = async () => {
    if (!staged) return;
    setLoading(true);
    setError(null);
    try {
      const lo = Math.max(1, Math.min(staged.pageCount, startPage));
      const hi = Math.max(lo, Math.min(staged.pageCount, endPage));
      const text = await staged.doc.extractText(lo, hi);
      if (!text.trim()) {
        setError(t('input.pdf.err_no_text'));
        setLoading(false);
        return;
      }
      addSource({
        kind: 'pdf',
        label: staged.file.name,
        text,
        page_range: [lo, hi],
        mime_type: 'application/pdf',
        byte_size: staged.file.size,
      });
      await staged.doc.destroy().catch(() => undefined);
      setStaged(null);
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('input.pdf.err_extract'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="pdf-uploader">
      {!staged ? (
        <label
          className="flex flex-col items-center justify-center gap-2 px-4 py-8 border-[2.5px] border-dashed border-ink rounded-card bg-paper hover:bg-white/60 cursor-pointer text-center font-hand text-ink"
          data-testid="pdf-dropzone"
        >
          <span className="text-4xl" aria-hidden>
            📄
          </span>
          <span className="text-[15px]">{t('input.pdf.cta')}</span>
          <span className="font-caveat text-ink-soft text-[14px]">{t('input.pdf.hint')}</span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFile(f);
              e.target.value = '';
            }}
            disabled={loading}
            data-testid="pdf-file-input"
          />
        </label>
      ) : (
        <div className="flex gap-3 items-start">
          {staged.thumb ? (
            <img
              src={staged.thumb}
              alt={`Aperçu de ${staged.file.name}`}
              className="w-[120px] h-auto border-2 border-ink rounded-control shadow-ink-sm shrink-0"
            />
          ) : (
            <div className="w-[120px] h-[160px] border-2 border-ink rounded-control bg-paper grid place-items-center font-hand text-[12px] text-ink-soft">
              {t('input.pdf.preview_unavailable')}
            </div>
          )}
          <div className="flex-1 flex flex-col gap-2">
            <div className="font-hand text-[15px] text-ink truncate" title={staged.file.name}>
              {staged.file.name}
            </div>
            <div className="font-caveat text-ink-soft text-[15px]">
              {t(staged.pageCount === 1 ? 'input.pdf.page_one' : 'input.pdf.page_other', {
                n: staged.pageCount,
              })}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="font-hand text-[14px] flex items-center gap-1">
                {t('input.pdf.from')}
                <input
                  type="number"
                  min={1}
                  max={staged.pageCount}
                  value={startPage}
                  onChange={(e) =>
                    setStartPage(
                      Math.max(1, Math.min(staged.pageCount, Number(e.target.value) || 1)),
                    )
                  }
                  className="w-16 px-2 py-1 border-2 border-ink rounded-control bg-paper text-center"
                />
              </label>
              <label className="font-hand text-[14px] flex items-center gap-1">
                {t('input.pdf.to')}
                <input
                  type="number"
                  min={startPage}
                  max={staged.pageCount}
                  value={endPage}
                  onChange={(e) =>
                    setEndPage(
                      Math.max(startPage, Math.min(staged.pageCount, Number(e.target.value) || 1)),
                    )
                  }
                  className="w-16 px-2 py-1 border-2 border-ink rounded-control bg-paper text-center"
                />
              </label>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={extract}
                disabled={loading}
                className="px-3 py-2 min-h-[40px] bg-accent-blue text-white font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-40"
                data-testid="pdf-extract"
              >
                {loading ? t('input.pdf.extracting') : t('input.pdf.extract')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  await staged.doc.destroy().catch(() => undefined);
                  setStaged(null);
                }}
                disabled={loading}
                className="px-3 py-2 min-h-[40px] bg-white text-ink font-hand text-[14px] border-2 border-ink rounded-control shadow-ink-sm disabled:opacity-40"
              >
                {t('input.pdf.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      {error ? (
        <p className="font-hand text-warn-text text-[14px]" role="alert">
          ⚠️ {error}
        </p>
      ) : null}
      {loading && !staged ? (
        <p className="font-caveat text-ink-soft text-[15px]">{t('input.pdf.loading')}</p>
      ) : null}
    </div>
  );
}
